<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SectionCard from 'dashboard/components-next/section-card/SectionCard.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import SpaceDescription from '../spaces/components/SpaceDescription.vue';
import BookableResourcesAPI from 'dashboard/api/planning/bookableResources';
import ResourceIconPicker from './ResourceIconPicker.vue';
import { resourceDisplayIcon } from './resourceIcons';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const loading = ref(true);
const saving = ref(false);
const resource = ref(null);
const editingName = ref(false);
const tempName = ref('');
const showDeleteModal = ref(false);
const avatarPreviewUrl = ref('');

const form = ref({
  kind: 'equipment',
  capacity: 1,
  active: true,
  icon: null,
});

const kinds = computed(() => [
  { id: 'equipment', label: t('SETTINGS.RESOURCES.KINDS.EQUIPMENT') },
  { id: 'person', label: t('SETTINGS.RESOURCES.KINDS.PERSON') },
  { id: 'role', label: t('SETTINGS.RESOURCES.KINDS.ROLE') },
]);

const resourceId = computed(() => route.params.resourceId);

const avatarSrc = computed(
  () => avatarPreviewUrl.value || resource.value?.thumbnail || ''
);

const headerIcon = computed(() =>
  resource.value ? resourceDisplayIcon(resource.value) : 'i-lucide-package'
);

const syncForm = record => {
  form.value = {
    kind: record.kind,
    capacity: record.capacity,
    active: record.active,
    icon: record.icon || null,
  };
};

const fetchResource = async () => {
  loading.value = true;
  try {
    const { data } = await BookableResourcesAPI.show(resourceId.value);
    resource.value = data;
    avatarPreviewUrl.value = '';
    syncForm(data);
  } catch (e) {
    useAlert(e.message || t('SETTINGS.RESOURCES.LOAD_ERROR'));
    router.push({ name: 'settings_resources_index' });
  } finally {
    loading.value = false;
  }
};

const handleBack = () => {
  router.push({ name: 'settings_resources_index' });
};

const startEditingName = () => {
  tempName.value = resource.value?.name || '';
  editingName.value = true;
};

const cancelEditingName = () => {
  editingName.value = false;
  tempName.value = '';
};

const saveResource = async (attrs = {}) => {
  if (!resource.value) return;
  saving.value = true;
  try {
    const { data } = await BookableResourcesAPI.update(resource.value.id, attrs);
    resource.value = data;
    syncForm(data);
    return data;
  } catch (e) {
    useAlert(
      e?.response?.data?.errors?.base ||
        e.message ||
        t('SETTINGS.RESOURCES.SAVE_ERROR')
    );
    throw e;
  } finally {
    saving.value = false;
  }
};

const saveName = async () => {
  const name = tempName.value.trim();
  if (!name) {
    useAlert(t('SETTINGS.RESOURCES.NAME_REQUIRED'));
    return;
  }
  try {
    await saveResource({ name });
    editingName.value = false;
  } catch {
    // alert already shown
  }
};

const saveDescription = async html => {
  if (!resource.value) return;
  try {
    await saveResource({ description: html });
  } catch {
    // alert already shown
  }
};

const saveIcon = async icon => {
  form.value.icon = icon;
  try {
    await saveResource({ icon });
  } catch {
    // alert already shown
  }
};

const saveDetails = async () => {
  try {
    await saveResource({
      kind: form.value.kind,
      capacity: Number(form.value.capacity) || 1,
      active: form.value.active,
      icon: form.value.icon,
    });
    useAlert(t('SETTINGS.RESOURCES.UPDATE_SUCCESS'));
  } catch {
    // alert already shown
  }
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarPreviewUrl.value = url;
  try {
    const data = await saveResource({ avatar: file });
    avatarPreviewUrl.value = data?.thumbnail || url;
    useAlert(t('SETTINGS.RESOURCES.PICTURE.UPLOAD_SUCCESS'));
  } catch {
    avatarPreviewUrl.value = resource.value?.thumbnail || '';
  }
};

const handleAvatarDelete = async () => {
  if (!resource.value) return;
  try {
    await BookableResourcesAPI.deleteAvatar(resource.value.id);
    avatarPreviewUrl.value = '';
    resource.value = { ...resource.value, thumbnail: '' };
    useAlert(t('SETTINGS.RESOURCES.PICTURE.DELETE_SUCCESS'));
  } catch (e) {
    useAlert(e.message || t('SETTINGS.RESOURCES.PICTURE.DELETE_ERROR'));
  }
};

const confirmDelete = async () => {
  if (!resource.value) return;
  try {
    await BookableResourcesAPI.delete(resource.value.id);
    showDeleteModal.value = false;
    router.push({ name: 'settings_resources_index' });
  } catch (e) {
    useAlert(e.message || t('SETTINGS.RESOURCES.DELETE_ERROR'));
  }
};

onMounted(fetchResource);
</script>

<template>
  <div class="flex h-full w-full flex-col bg-white dark:bg-slate-900">
    <div
      v-if="loading"
      class="flex flex-1 items-center justify-center text-n-slate-11"
    >
      {{ t('SETTINGS.RESOURCES.LOADING') }}
    </div>

    <div
      v-else-if="resource"
      class="flex h-full flex-col overflow-hidden bg-n-surface-1"
    >
      <div class="shrink-0 bg-n-surface-1 py-5">
        <div
          class="mx-auto flex w-full max-w-7xl items-center justify-between px-6"
        >
          <div class="flex items-center gap-4">
            <button
              type="button"
              class="flex items-center rounded-md bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-700 transition-colors hover:bg-purple-100"
              @click="handleBack"
            >
              <Icon icon="i-lucide-undo-2" class="mr-2 size-4" />
              {{ t('SETTINGS.RESOURCES.ALL_RESOURCES') }}
            </button>
            <Avatar
              v-if="avatarSrc"
              :src="avatarSrc"
              :name="resource.name"
              :size="40"
              rounded-full
            />
            <div
              v-else
              class="flex size-10 items-center justify-center rounded-full bg-n-brand/10 text-n-brand"
            >
              <Icon :icon="headerIcon" class="size-5" />
            </div>
            <div v-if="editingName" class="flex items-center gap-2">
              <input
                v-model="tempName"
                type="text"
                class="reset-base no-margin min-w-[300px] border-b-2 border-purple-500 bg-transparent px-2 py-1 text-xl font-bold text-slate-900 focus:outline-none dark:text-white"
                autofocus
                @keyup.enter="saveName"
                @keyup.escape="cancelEditingName"
              />
              <button
                type="button"
                class="p-1 text-purple-600 hover:text-purple-700"
                :disabled="saving"
                @click="saveName"
              >
                <Icon icon="i-lucide-check" class="size-5" />
              </button>
              <button
                type="button"
                class="p-1 text-slate-400 hover:text-slate-600"
                @click="cancelEditingName"
              >
                <Icon icon="i-lucide-x" class="size-5" />
              </button>
            </div>
            <h1
              v-else
              class="cursor-pointer text-xl font-bold text-slate-900 transition-colors hover:text-purple-600 dark:text-white"
              :title="t('SETTINGS.RESOURCES.CLICK_TO_EDIT')"
              @click="startEditingName"
            >
              {{ resource.name }}
            </h1>
          </div>

          <button
            type="button"
            class="flex h-10 w-10 items-center justify-center rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-600 hover:text-white"
            @click="showDeleteModal = true"
          >
            <Icon icon="i-lucide-trash-2" class="size-5" />
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        <div class="mx-auto max-w-7xl space-y-8 px-6 py-8">
          <SectionCard
            :show-drag-handle="false"
            :title="t('SETTINGS.RESOURCES.DESCRIPTION_SECTION.TITLE')"
            icon="i-lucide-file-text"
          >
            <SpaceDescription
              :description="resource.description"
              :saving="saving"
              @save="saveDescription"
            />
          </SectionCard>

          <SectionCard
            :show-drag-handle="false"
            :title="t('SETTINGS.RESOURCES.ICON.TITLE')"
            icon="i-lucide-sparkles"
          >
            <p class="mb-4 text-sm text-n-slate-11">
              {{ t('SETTINGS.RESOURCES.ICON.HINT') }}
            </p>
            <ResourceIconPicker
              :model-value="form.icon"
              :fallback-icon="headerIcon"
              @update:model-value="saveIcon"
            />
          </SectionCard>

          <SectionCard
            :show-drag-handle="false"
            :title="t('SETTINGS.RESOURCES.PICTURE.TITLE')"
            icon="i-lucide-image"
          >
            <p class="mb-4 text-sm text-n-slate-11">
              {{ t('SETTINGS.RESOURCES.PICTURE.HINT') }}
            </p>
            <Avatar
              :src="avatarSrc"
              :name="resource.name"
              :size="120"
              allow-upload
              rounded-full
              @upload="handleAvatarUpload"
              @delete="handleAvatarDelete"
            />
          </SectionCard>

          <SectionCard
            :show-drag-handle="false"
            :title="t('SETTINGS.RESOURCES.DETAILS.TITLE')"
            icon="i-lucide-package"
          >
            <div class="grid max-w-xl gap-5">
              <div>
                <label class="mb-1 block text-sm font-medium text-n-slate-11">
                  {{ t('SETTINGS.RESOURCES.KIND') }}
                </label>
                <select
                  v-model="form.kind"
                  class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-n-slate-12"
                  :disabled="saving"
                >
                  <option v-for="k in kinds" :key="k.id" :value="k.id">
                    {{ k.label }}
                  </option>
                </select>
              </div>
              <div>
                <label class="mb-1 block text-sm font-medium text-n-slate-11">
                  {{ t('SETTINGS.RESOURCES.CAPACITY') }}
                </label>
                <input
                  v-model.number="form.capacity"
                  type="number"
                  min="1"
                  class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-n-slate-12"
                  :disabled="saving"
                />
                <p class="mt-1 text-xs text-n-slate-11">
                  {{ t('SETTINGS.RESOURCES.DETAILS.CAPACITY_HINT') }}
                </p>
              </div>
              <div class="flex items-center gap-3">
                <Switch v-model="form.active" />
                <span class="text-sm font-semibold text-n-slate-12">
                  {{ t('SETTINGS.RESOURCES.FORM.ACTIVE_LABEL') }}
                </span>
              </div>
              <div>
                <Button
                  :label="t('SETTINGS.RESOURCES.SAVE')"
                  :is-loading="saving"
                  @click="saveDetails"
                />
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>

    <div
      v-if="showDeleteModal"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
      @click.self="showDeleteModal = false"
    >
      <div
        class="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-slate-800"
      >
        <h3 class="text-base font-semibold text-slate-900 dark:text-white">
          {{ t('SETTINGS.RESOURCES.DELETE_MODAL.TITLE') }}
        </h3>
        <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">
          {{ t('SETTINGS.RESOURCES.DELETE_MODAL.DESCRIPTION_PRE') }}
          <span class="font-semibold">{{ resource?.name }}</span>
          {{ t('SETTINGS.RESOURCES.DELETE_MODAL.DESCRIPTION_POST') }}
        </p>
        <div class="mt-5 flex items-center gap-2">
          <Button
            :label="t('SETTINGS.RESOURCES.CANCEL')"
            slate
            outlined
            class="w-full"
            @click="showDeleteModal = false"
          />
          <Button
            :label="t('SETTINGS.RESOURCES.DELETE_MODAL.CONFIRM')"
            ruby
            class="w-full"
            @click="confirmDelete"
          />
        </div>
      </div>
    </div>
  </div>
</template>
